import type { VNode } from "preact";
import { useEffect, useState } from "preact/hooks";
import {
  Keys,
  clearAllStorage,
  clearMyBias,
  dumpStorage,
  getMyBias,
  getTipsAcked,
  peekSetting,
  reackTip,
  removeMyBias,
  removeSetting,
  resetTipsAcked,
  restoreMyBias,
  restoreSetting,
  restoreStorageSnapshot,
  snapshotStorage,
  unackTip,
  type StorageRow,
} from "../settings";
import { tipMeta, type TipMeta } from "../lib/tip-labels";
import type { Toast } from "./Toast";

/**
 * The complete settings UI — every section, shared by BOTH hosts:
 *
 *   - SettingsModal  → the popup dialog from the header gear button
 *   - SettingsPage   → the standalone /settings route
 *
 * All storage mutations funnel through here so the two surfaces can never
 * drift apart. Feedback goes to the host via `onToast`; after anything that
 * changes persisted state the host is told via `onStorageChanged` so it can
 * re-sync its own localStorage-derived state.
 *
 * Destructive actions (per-row reset, per-tip reset, per-domain bias reset,
 * reset-all, clear-all) snapshot what they are about to remove and attach an
 * Undo button to their toast. Restores are timid: they only write values
 * back that are currently absent, so an undo never clobbers a newer choice
 * (see the undo helpers in src/settings.ts).
 */
type Props = {
  redirectX: boolean;
  onRedirectX: (on: boolean) => void;
  customSource: string;
  onCustomSource: (url: string) => void;
  preferred: string | null;
  onClearPreferred: () => void;
  onToast?: (t: Omit<Toast, "id">) => void;
  /** Called after ANY persisted setting changed (per-row reset, tooltip
   *  reset, clear all) so the host can re-read localStorage. */
  onStorageChanged?: () => void;
  /** Sections the host wants expanded (e.g. /settings#tooltips). Applied on
   *  mount AND whenever the host flips a flag (same-document hash change). */
  openSections?: { priority?: boolean; tooltips?: boolean };
};

/* --- tiny inline icons ----------------------------------------------------- */

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function RotateCcwIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}

function ChevronIcon({ up }: { up?: boolean }) {
  return (
    <svg
      width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"
      style={{ transform: up ? "rotate(180deg)" : undefined, transition: "transform .15s ease" }}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/** "acknowledged 4m ago" style stamp for one tip entry. */
function fmtAckedAt(at: number): string {
  const s = Math.max(0, Math.floor((Date.now() - at) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `on ${new Date(at).toLocaleDateString()}`;
}

/** Most recently acknowledged first — the list reads like a small history. */
function sortedAcked(map: Record<string, number>): [string, number][] {
  return Object.entries(map).sort((a, b) => b[1] - a[1]);
}

/** Client-side mirror of the server's source-URL guard (functions/api/
 *  poll.ts): https only, public-looking hostname — so what the UI accepts
 *  is exactly what the poll endpoint will accept. Empty string = clear
 *  (always allowed) and is handled by the caller. */
function validateSourceUrl(raw: string): string | null {
  try {
    const u = new URL(raw.trim());
    if (u.protocol !== "https:") return "must use https://";
    if (!u.hostname.includes(".")) return "hostname looks incomplete";
    if (/^(localhost|127\.|10\.|192\.168\.|169\.254\.|0\.0\.0\.0|\[?::1)/i.test(u.hostname))
      return "private/loopback addresses cannot be polled by the server";
    if (/\.local$/i.test(u.hostname))
      return "private/loopback addresses cannot be polled by the server";
    return null;
  } catch {
    return "not a valid URL";
  }
}

/** Signed formatting for a bias tally: 2 → "+2", -1 → "-1". */
const fmtBias = (v: number) => `${v > 0 ? "+" : ""}${v}`;

/* --- tooltip grouping ------------------------------------------------------- */

type AckedTip = { key: string; at: number; meta: TipMeta };

/** Canonical display order of tip areas; anything else (future areas, "Unknown") is appended alphabetically at the end. */
const TIP_GROUP_ORDER = ["Header", "Instance rows", "toolbar", "pill"];

/** Group acknowledged tips by the area their element lives in, keeping TIP_GROUP_ORDER first and sorting the tail (incl. "Unknown") last. */
function groupTips(entries: AckedTip[]): { where: string; tips: AckedTip[] }[] {
  const byWhere = new Map<string, AckedTip[]>();
  for (const e of entries) {
    const list = byWhere.get(e.meta.where) ?? [];
    list.push(e);
    byWhere.set(e.meta.where, list);
  }
  const groups: { where: string; tips: AckedTip[] }[] = [];
  for (const where of TIP_GROUP_ORDER) {
    const tips = byWhere.get(where);
    if (tips?.length) groups.push({ where, tips });
    byWhere.delete(where);
  }
  const rest = [...byWhere.keys()].sort((a, b) =>
    a === "Unknown" ? 1 : b === "Unknown" ? -1 : a.localeCompare(b),
  );
  for (const where of rest) groups.push({ where, tips: byWhere.get(where)! });
  return groups;
}

type SourceTest =
  | { status: "testing" }
  | { status: "ok"; msg: string }
  | { status: "fail"; msg: string };

export function SettingsPanel({
  redirectX,
  onRedirectX,
  customSource,
  onCustomSource,
  preferred,
  onClearPreferred,
  onToast,
  onStorageChanged,
  openSections,
}: Props): VNode {
  const [srcDraft, setSrcDraft] = useState(customSource);
  const [srcError, setSrcError] = useState<string | null>(null);
  const [srcTest, setSrcTest] = useState<SourceTest | null>(null);
  const [tipCount, setTipCount] = useState(Object.keys(getTipsAcked()).length);
  const [tipsOpen, setTipsOpen] = useState(openSections?.tooltips ?? false);
  const [tipFilter, setTipFilter] = useState("");
  const [closedGroups, setClosedGroups] = useState<Record<string, boolean>>({});
  const [biasCount, setBiasCount] = useState(Object.keys(getMyBias()).length);
  const [biasOpen, setBiasOpen] = useState(openSections?.priority ?? false);
  const [rows, setRows] = useState<StorageRow[]>(dumpStorage);

  // Same-document hash changes (e.g. a link to /settings#priority clicked
  // while the page is already open) re-open the matching disclosure.
  useEffect(() => {
    if (openSections?.tooltips) setTipsOpen(true);
    if (openSections?.priority) setBiasOpen(true);
  }, [openSections?.tooltips, openSections?.priority]);

  const refresh = () => {
    setTipCount(Object.keys(getTipsAcked()).length);
    setBiasCount(Object.keys(getMyBias()).length);
    setRows(dumpStorage());
  };

  /** Standard "the undo window has passed / state moved on" feedback. */
  const undoMissed = () =>
    onToast?.({ kind: "info", text: "Nothing to undo — the settings changed since." });

  /* --- data source: validate + test ---------------------------------------- */

  const commitSource = () => {
    const trimmed = srcDraft.trim();
    if (!trimmed) {
      // Clearing back to the server default is always allowed.
      setSrcError(null);
      setSrcTest(null);
      onCustomSource("");
      return;
    }
    const err = validateSourceUrl(trimmed);
    if (err) {
      setSrcError(err);
      onToast?.({ kind: "error", text: `Not saved — the URL ${err}.` });
      return;
    }
    setSrcError(null);
    onCustomSource(trimmed);
  };

  const testSource = async () => {
    const url = srcDraft.trim();
    if (!url) {
      setSrcTest({ status: "fail", msg: "Nothing to test — enter a feed URL first." });
      return;
    }
    const err = validateSourceUrl(url);
    if (err) {
      setSrcTest({ status: "fail", msg: `Cannot test: the URL ${err}.` });
      return;
    }
    setSrcTest({ status: "testing" });
    try {
      const res = await fetch(url, {
        headers: { accept: "application/json" },
        credentials: "omit",
      });
      if (!res.ok) {
        setSrcTest({ status: "fail", msg: `The feed answered HTTP ${res.status}.` });
        return;
      }
      let data: unknown;
      try {
        data = await res.json();
      } catch {
        setSrcTest({ status: "fail", msg: "The response is not valid JSON." });
        return;
      }
      const hosts = (data as { hosts?: unknown[] })?.hosts;
      if (!Array.isArray(hosts)) {
        setSrcTest({ status: "fail", msg: "The JSON has no “hosts” array — this is not an uptime feed." });
        return;
      }
      const healthy = hosts.filter((h) => (h as { healthy?: boolean })?.healthy === true).length;
      const lastRaw = (data as { last_update?: string })?.last_update;
      const last = lastRaw ? new Date(lastRaw) : null;
      const lastOk = last && !isNaN(last.getTime()) ? `, last update ${last.toLocaleString()}` : "";
      setSrcTest({
        status: "ok",
        msg: `Feed looks good: ${hosts.length} host${hosts.length === 1 ? "" : "s"}, ${healthy} healthy${lastOk}.`,
      });
    } catch {
      setSrcTest({
        status: "fail",
        msg: "The browser could not fetch it (offline or CORS-blocked). The server may still reach it — “Poll now” is the real test.",
      });
    }
  };

  /* --- destructive actions, each with Undo ---------------------------------- */

  const doResetTips = () => {
    const snap = peekSetting(Keys.tipsAcked);
    resetTipsAcked();
    refresh();
    onToast?.({
      kind: "success",
      text: "Tooltips reset — they will show once more.",
      action: snap != null
        ? {
            label: "Undo",
            onClick: () => {
              if (restoreSetting(Keys.tipsAcked, snap)) {
                refresh();
                onToast?.({ kind: "info", text: "Tooltips restored." });
              } else undoMissed();
            },
          }
        : undefined,
    });
  };

  /** Un-acknowledge ONE tooltip: it shows again on the next hover. */
  const doUnackTip = (key: string) => {
    const meta = tipMeta(key);
    const at = getTipsAcked()[key];
    unackTip(key);
    refresh();
    onToast?.({
      kind: "success",
      text: `“${meta.label}” tip will show again.`,
      action: at != null
        ? {
            label: "Undo",
            onClick: () => {
              if (reackTip(key, at)) {
                refresh();
                onToast?.({ kind: "info", text: `“${meta.label}” tip re-acknowledged.` });
              } else undoMissed();
            },
          }
        : undefined,
    });
  };

  /** Reset EVERY acknowledged tip within one area (group header button). */
  const doResetTipGroup = (group: { where: string; tips: AckedTip[] }) => {
    const snap: Record<string, number> = {};
    for (const t of group.tips) {
      const at = getTipsAcked()[t.key];
      if (at != null) snap[t.key] = at;
      unackTip(t.key);
    }
    refresh();
    const n = Object.keys(snap).length;
    if (!n) return;
    onToast?.({
      kind: "success",
      text: `${n} tip${n === 1 ? "" : "s"} in “${group.where}” will show again.`,
      action: {
        label: "Undo",
        onClick: () => {
          let restored = 0;
          for (const [k, at] of Object.entries(snap)) if (reackTip(k, at)) restored++;
          refresh();
          if (restored)
            onToast?.({ kind: "info", text: `Restored ${restored} tip${restored === 1 ? "" : "s"}.` });
          else undoMissed();
        },
      },
    });
  };

  /** Reset one individual setting (trash icon on a stored row). */
  const doRemoveRow = (row: StorageRow) => {
    const snap = peekSetting(row.key);
    if (!removeSetting(row.key)) return;
    refresh();
    onStorageChanged?.();
    onToast?.({
      kind: "success",
      text: `Reset “${row.label}” — back to the default.`,
      action: snap != null
        ? {
            label: "Undo",
            onClick: () => {
              if (restoreSetting(row.key, snap)) {
                refresh();
                onStorageChanged?.();
                onToast?.({ kind: "info", text: `Restored “${row.label}”.` });
              } else undoMissed();
            },
          }
        : undefined,
    });
  };

  /** Reset ONE domain's boost/penalize tally (priority adjustments list). */
  const doRemoveBias = (domain: string) => {
    const prev = removeMyBias(domain);
    if (prev == null) return;
    refresh();
    onStorageChanged?.();
    onToast?.({
      kind: "success",
      text: `Removed your ${fmtBias(prev)} adjustment for ${domain}.`,
      action: {
        label: "Undo",
        onClick: () => {
          if (restoreMyBias(domain, prev)) {
            refresh();
            onStorageChanged?.();
            onToast?.({ kind: "info", text: `Restored ${fmtBias(prev)} for ${domain}.` });
          } else undoMissed();
        },
      },
    });
  };

  /** Clear the whole local bias record (“Reset all” in priority section). */
  const doClearBias = () => {
    const snap = peekSetting(Keys.myBias);
    clearMyBias();
    refresh();
    onStorageChanged?.();
    onToast?.({
      kind: "success",
      text: "All priority adjustments reset.",
      action: snap != null
        ? {
            label: "Undo",
            onClick: () => {
              if (restoreSetting(Keys.myBias, snap)) {
                refresh();
                onStorageChanged?.();
                onToast?.({ kind: "info", text: "Priority adjustments restored." });
              } else undoMissed();
            },
          }
        : undefined,
    });
  };

  const doClearStorage = () => {
    const snap = snapshotStorage();
    clearAllStorage();
    refresh();
    onStorageChanged?.();
    const n = Object.keys(snap).length;
    onToast?.({
      kind: "success",
      text: n ? `Browser storage cleared (${n} setting${n === 1 ? "" : "s"}).` : "Browser storage cleared.",
      action: n
        ? {
            label: "Undo",
            onClick: () => {
              const restored = restoreStorageSnapshot(snap);
              refresh();
              onStorageChanged?.();
              if (restored)
                onToast?.({ kind: "info", text: `Restored ${restored} setting${restored === 1 ? "" : "s"}.` });
              else undoMissed();
            },
          }
        : undefined,
    });
  };

  /* --- derived views --------------------------------------------------------- */

  // Priority adjustments, strongest tally first (reads like a ranking).
  const biasEntries = Object.entries(getMyBias()).sort(
    (a, b) => Math.abs(b[1]) - Math.abs(a[1]) || a[0].localeCompare(b[0]),
  );

  const ackedTips: AckedTip[] = sortedAcked(getTipsAcked()).map(([key, at]) => ({
    key,
    at,
    meta: tipMeta(key),
  }));
  const q = tipFilter.trim().toLowerCase();
  const visibleTips = q
    ? ackedTips.filter((t) =>
        `${t.meta.label} ${t.meta.where} ${t.key}`.toLowerCase().includes(q),
      )
    : ackedTips;
  const tipGroups = groupTips(visibleTips);

  return (
    <>
      {/*<p class="set__hint">All your settings persist locally in this browser (localStorage) and are never sent to the server. Each stored entry below can be reset individually with the <TrashIcon /> button — a toast with an <b>Undo</b> button gives you a few seconds to change your mind.</p>*/}

      {/* Viewing */}
      <section class="set" id="set-redirect">
        <h3 class="set__title">Redirect</h3>
        <label class="set__row">
          <input
            type="checkbox"
            checked={redirectX}
            onChange={(e) => onRedirectX((e.target as HTMLInputElement).checked)}
          />
          <span class="set__row-text">
            <b>Redirect to X.com</b>
            <small>
              When enabled, URLs skip the Nitter proxy
              entirely and take you straight to <code>https://x.com/…</code>.
            </small>
          </span>
        </label>
      </section>

      {/* Data source */}
      <section class="set" id="set-data-source">
        <h3 class="set__title">Data source</h3>
        <p class="set__hint">
          Instance health comes from an uptime-JSON feed that the tracker polls.
          Point it at a different feed here.{" "}
          <a href="/data-source">Read the data-source docs &amp; schema →</a>
        </p>
        <div class="set__input-row">
          <input
            type="url"
            placeholder="https://example.org/nitter-uptime.json  (empty = default feed)"
            value={srcDraft}
            onInput={(e) => {
              setSrcDraft((e.target as HTMLInputElement).value);
              setSrcError(null);
            }}
            onKeyDown={(e) => e.key === "Enter" && commitSource()}
            spellcheck={false}
            autocapitalize="none"
            aria-invalid={srcError ? "true" : undefined}
          />
          <button
            class="btn btn--sm"
            onClick={testSource}
            disabled={srcTest?.status === "testing"}
          >
            {srcTest?.status === "testing" ? "Testing…" : "Test"}
          </button>
          <button class="btn btn--sm btn--primary" onClick={commitSource}>Save</button>
        </div>
        {srcError && (
          <small class="set__src-error" role="alert">
            Not saved — the URL {srcError}. Must be a public https:// JSON feed.
          </small>
        )}
        {srcTest && srcTest.status !== "testing" && (
          <small
            class={`set__test${
              srcTest.status === "ok" ? " set__test--ok" : " set__test--fail"
            }`}
            role="status"
          >
            {srcTest.msg}
          </small>
        )}
        <small class="set__note">
          Stored in this browser only. Used as the feed source when you press “Poll&nbsp;now”.
        </small>
      </section>

      {/* Preferred instance */}
      <section class="set" id="set-preferred">
        <h3 class="set__title">Preferred instance</h3>
        {preferred ? (
          <div class="set__row set__row--static">
            <code class="set__code">{preferred}</code>
            <button class="btn btn--sm" onClick={onClearPreferred}>Clear</button>
          </div>
        ) : (
          <p class="set__hint">None yet. Click the ★ “Prefer” button on any instance to favorite it.</p>
        )}
      </section>

      {/* Priority adjustments — per-domain boost/penalize tallies */}
      <section class="set" id="set-priority">
        <h3 class="set__title">Priority adjustments</h3>
        <div class="set__row set__row--static">
          <span class="set__row-text">
            <b>
              {biasCount} domain{biasCount === 1 ? "" : "s"} adjusted
            </b>
            <small>
              Your Boost/Penalize tallies per instance, saved in this browser and
              merged into the priority bars you see. Expand the list to reset one
              domain.
            </small>
          </span>
          <span class="set__tips-actions">
            <button
              class="btn btn--sm"
              onClick={() => setBiasOpen((o) => !o)}
              disabled={biasCount === 0}
              aria-expanded={biasOpen}
              aria-controls="bias-list"
            >
              {biasOpen ? "Hide list" : "Show which"}
              <ChevronIcon up={biasOpen} />
            </button>
            <button class="btn btn--sm" onClick={doClearBias} disabled={biasCount === 0}>
              Reset all
            </button>
          </span>
        </div>

        {biasOpen && (
          biasCount === 0 ? (
            <p class="set__hint set__hint--empty" id="bias-list">
              No adjustments — use the ▲ Boost / ▼ Penalize buttons on an
              instance row (or in the /view toolbar) and it will show up here.
            </p>
          ) : (
            <ul class="set__tips" id="bias-list">
              {biasEntries.map(([domain, v]) => (
                <li key={domain}>
                  <div class="set__tips-text">
                    <b>{domain}</b>
                    <small>
                      your tally:{" "}
                      <code class={v > 0 ? "set__bias-pos" : "set__bias-neg"}>{fmtBias(v)}</code>{" "}
                      — merged into its priority bar
                    </small>
                  </div>
                  <span class="set__tips-time">{v > 0 ? "boosted" : "penalized"}</span>
                  <button
                    class="btn btn--ghost btn--icon set__tips-reset"
                    aria-label={`Reset adjustment for ${domain}`}
                    title={`Reset adjustment for ${domain}`}
                    onClick={() => doRemoveBias(domain)}
                  >
                    <TrashIcon />
                  </button>
                </li>
              ))}
            </ul>
          )
        )}
      </section>

      {/* Tooltips — count, expandable per-area groups, per-tip reset */}
      <section class="set" id="set-tooltips">
        <h3 class="set__title">Tooltips</h3>
        <div class="set__row set__row--static">
          <span class="set__row-text">
            <b>{tipCount} tooltip{tipCount === 1 ? "" : "s"} acknowledged</b>
            <small>
              Tips show once and are considered acknowledged once the small
              progress bar is filled up. Need one again? Hold{" "}
              <kbd class="set__kbd">Ctrl</kbd> or <kbd class="set__kbd">Shift</kbd>{" "}
              while hovering the element, expand the list below to reset tips
              individually, or press “Reset&nbsp;all”.
            </small>
          </span>
          <span class="set__tips-actions">
            <button
              class="btn btn--sm"
              onClick={() => setTipsOpen((o) => !o)}
              disabled={tipCount === 0}
              aria-expanded={tipsOpen}
              aria-controls="acked-tips-list"
            >
              {tipsOpen ? "Hide list" : "Show which"}
              <ChevronIcon up={tipsOpen} />
            </button>
            <button class="btn btn--sm" onClick={doResetTips} disabled={tipCount === 0}>
              Reset all
            </button>
          </span>
        </div>

        {tipsOpen && (
          tipCount === 0 ? (
            <p class="set__hint set__hint--empty" id="acked-tips-list">
              No tooltips acknowledged — hover any tipped element and let its
              progress bar fill up (or click it) to see it listed here.
            </p>
          ) : (
            <div id="acked-tips-list">
              <input
                class="set__tips-filter"
                type="search"
                placeholder="Filter by name, area or key…"
                value={tipFilter}
                onInput={(e) => setTipFilter((e.target as HTMLInputElement).value)}
                aria-label="Filter acknowledged tooltips"
              />
              {tipGroups.length === 0 ? (
                <p class="set__hint set__hint--empty">No tips match “{tipFilter}”.</p>
              ) : (
                tipGroups.map((group) => {
                  const collapsed = closedGroups[group.where] ?? false;
                  return (
                    <div class="set__tips-group" key={group.where}>
                      <div class="set__tips-group-head">
                        <button
                          class="set__tips-group-toggle"
                          onClick={() =>
                            setClosedGroups((c) => ({ ...c, [group.where]: !c[group.where] }))
                          }
                          aria-expanded={!collapsed}
                        >
                          <ChevronIcon up={!collapsed} />
                          <b>{group.where}</b>
                          <span class="set__tips-group-count">{group.tips.length}</span>
                        </button>
                        <button
                          class="btn btn--ghost btn--sm set__tips-group-reset"
                          onClick={() => doResetTipGroup(group)}
                          title={`Reset all “${group.where}” tips`}
                        >
                          Reset
                        </button>
                      </div>
                      {!collapsed && (
                        <ul class="set__tips">
                          {group.tips.map((t) => (
                            <li key={t.key}>
                              <div class="set__tips-text">
                                <b>{t.meta.label}</b>
                                <small>
                                  <code>{t.key}</code>
                                </small>
                              </div>
                              <span class="set__tips-time" title={new Date(t.at).toLocaleString()}>
                                acknowledged {fmtAckedAt(t.at)}
                              </span>
                              <button
                                class="btn btn--ghost btn--icon set__tips-reset"
                                aria-label={`Show “${t.meta.label}” tip again`}
                                title={`Show “${t.meta.label}” tip again`}
                                onClick={() => doUnackTip(t.key)}
                              >
                                <RotateCcwIcon />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )
        )}
      </section>

      {/* Stored settings — live localStorage inventory, per-row reset */}
      <section class="set" id="set-storage">
        <h3 class="set__title">Stored settings</h3>
        {rows.length === 0 ? (
          <p class="set__hint set__hint--empty">Nothing stored yet.</p>
        ) : (
          <ul class="set__storage">
            {rows.map((r) => (
              <li key={r.key}>
                <span class="set__storage-label">{r.label}</span>
                <span class="set__storage-key">{r.key}</span>
                <button
                  class="btn btn--ghost btn--icon set__storage-del"
                  aria-label={`Reset “${r.label}”`}
                  title={`Reset “${r.label}”`}
                  onClick={() => doRemoveRow(r)}
                >
                  <TrashIcon />
                </button>
                <span class="set__storage-value">{r.value}</span>
              </li>
            ))}
          </ul>
        )}
        <button
          class="btn btn--sm btn--danger-ghost"
          onClick={doClearStorage}
          disabled={rows.length === 0}
        >
          Clear browser storage
        </button>
      </section>
    </>
  );
}
